import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import type { Location } from '../../types';

async function log(biz: string, type: string, desc: string, eId: string, uid: string) {
  try { await supabase.from('activity_log').insert({ business_id: biz, activity_type: type, description: desc, entity_type: 'location', entity_id: eId, logged_by: uid }); } catch { /* */ }
}

export interface LocationFilters {
  type: string | null;
  status: string | null;
  search: string;
}

export interface LocationSummary {
  activeCount: number;
  byType: { type: string; count: number }[];
}

export function useLocations(businessId: string | undefined) {
  const [all, setAll] = useState<Location[]>([]);
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<LocationFilters>({ type: null, status: null, search: '' });
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      const [locRes, clientsRes] = await Promise.all([
        supabase.from('locations').select('*').eq('business_id', businessId)
          .order('name').abortSignal(ctrl.signal),
        supabase.from('clients').select('id, name').eq('business_id', businessId)
          .abortSignal(ctrl.signal),
      ]);
      if (locRes.error) throw locRes.error;
      if (mountedRef.current) {
        setAll((locRes.data ?? []) as Location[]);
        setClients((clientsRes.data ?? []) as { id: string; name: string }[]);
      }
    } catch (e) { if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed'); }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  const rt = useMemo(() => ['locations'], []);
  useRealtimeSubscription(businessId, rt, fetch);

  const filtered = useMemo(() => {
    let list = all;
    if (filters.type) list = list.filter(l => l.type === filters.type);
    if (filters.status) list = list.filter(l => l.status === filters.status);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(l => l.name.toLowerCase().includes(q) || (l.address ?? '').toLowerCase().includes(q));
    }
    return list;
  }, [all, filters]);

  const summary = useMemo<LocationSummary>(() => {
    const active = all.filter(l => l.status === 'active');
    const typeMap = new Map<string, number>();
    for (const l of active) { typeMap.set(l.type, (typeMap.get(l.type) ?? 0) + 1); }
    const byType = Array.from(typeMap.entries()).map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count).slice(0, 3);
    return { activeCount: active.length, byType };
  }, [all]);

  const createLocation = useCallback(async (data: Partial<Location>, userId: string): Promise<string | null> => {
    if (!businessId) return null;
    try {
      const { data: loc, error: e } = await supabase.from('locations').insert({
        ...data, business_id: businessId, status: data.status ?? 'active', sort_order: all.length + 1,
      }).select('id').maybeSingle();
      if (e || !loc) return null;
      await log(businessId, 'location_created', `Location created: ${data.name}`, loc.id, userId);
      fetch(); return loc.id;
    } catch { return null; }
  }, [businessId, all.length, fetch]);

  const updateLocation = useCallback(async (id: string, data: Partial<Location>, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('locations').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, 'location_updated', `Location updated: ${data.name ?? id}`, id, userId);
      fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  const deactivate = useCallback(async (id: string, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('locations').update({ status: 'inactive', updated_at: new Date().toISOString() }).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, 'location_deactivated', 'Location deactivated', id, userId);
      fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  return { locations: filtered, allLocations: all, clients, loading, error, summary, filters, setFilters, createLocation, updateLocation, deactivate, refresh: fetch };
}
